import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export type StudioExecutionNodeStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed";
export type StudioExecutionPlanStatus =
  | "executed"
  | "failed"
  | "undone"
  | "running"
  | "suspended"
  | "cancelled";
export type StudioExecutionSource =
  | "chat-intent-chunk"
  | "chat-operation"
  | "webmcp"
  | "studio-ui";
export type StudioExecutionApprovalSource =
  | "not-required"
  | "awaiting-user-approval"
  | "not-approved"
  | "studio-user-action"
  | "webmcp-user-approval";

/**
 * Every `code` argument Studio's OWN services (`apps/cogo-studio/src/`)
 * actually pass to `CoogoExecutionError` (source of truth: `grep -rn
 * 'CoogoExecutionError(' src/ --include='*.ts'`, including the codes
 * threaded through `studio-orchestration-node-handlers.ts`'s `fail()`
 * helper). Kept as a closed enum, pinned source-derived by
 * `coogo-execution-error-code-scan.ts` (fix-round-1, I5) — a new throw site
 * with a new code makes that test fail, not just a comparison between two
 * hand-typed lists. A code that isn't in this list, or an error with no
 * code at all, resolves to `errors.unknown` instead of a missing/raw-English
 * line.
 *
 * SCOPE BOUNDARY (fix-round-1, I4 — read before citing this as "the closed
 * set of codes CoogoExecutionError is thrown with," which is only true for
 * apps/cogo-studio/src/): `CoogoExecutionError` is also thrown, with
 * different codes, from `packages/cogo-webmcp-runtime/src/orchestration/executor.ts`
 * — the lower-level orchestration executor, a separate architectural layer
 * this app depends on rather than owns. That file alone throws at least 24
 * distinct codes (measured directly, not estimated — see
 * task-1-report.md's fix-round-1 section for the full list and the
 * extraction method), of which 4 (`execution_lease_lost`,
 * `handler_not_registered`, `invalid_transition`, `journal_receipt_mismatch`)
 * already overlap this enum and so already resolve to locale copy; the
 * other ~20 (e.g. `dag_stalled`, `sibling_failed`, `run_already_active`,
 * `snapshot_version_mismatch`, `human_gate_expired`, ...), plus
 * `CoogoExecutionError`'s own `"execution_failed"` default code
 * (`packages/cogo-webmcp-runtime/src/orchestration/executor.ts:944,947`),
 * are NOT enumerated here. This is a deliberate, documented decision, not
 * an oversight: they already resolve safely today (any code not in this
 * enum falls through to the generic `errors.unknown` locale line — no raw
 * English leaks), and translating ~20 more codes across 5 locales for a
 * layer this app does not own is scoped out of task-1's display-boundary
 * repair. A future task that wants full customer-legible copy for executor
 * failures specifically should re-run the same extraction against that
 * file and fold the result in here.
 */
export const STUDIO_EXECUTION_ERROR_CODES = [
  "CONFIRMATION_STALE",
  "checkpoint_authority_mismatch",
  "durable_orchestration_auth_required",
  "exact_impact_invalid",
  "exact_impact_unavailable",
  "execution_lease_active",
  "execution_lease_lost",
  "handler_not_registered",
  "invalid_durable_event",
  "invalid_durable_plan",
  "invalid_transition",
  "journal_checkpoint_inconsistent",
  "journal_receipt_mismatch",
  "journal_replay_receipt_required",
  "journal_run_mismatch",
  "lease_receipt_mismatch",
  "manual_recovery_required",
  "mutation_outcome_evidence_missing",
  "mutation_outcome_not_unknown",
  "resume_authority_invalid",
  "resume_authority_rejected",
  "resume_grant_binding_mismatch",
  "resume_grant_checkpoint_mismatch",
  "studio_tool_failed",
  "undo_ownership_missing",
  "unsafe_transition_material",
] as const;

export type StudioExecutionErrorCode = (typeof STUDIO_EXECUTION_ERROR_CODES)[number];

export interface StudioExecutionCopy {
  taskCenter: {
    restoreFailure: string;
    latestExecution: (eventCount: number) => string;
    loadingDetails: string;
    attentionTitle: string;
    doneReviewing: string;
    continueInChat: string;
    targetUnavailable: string;
    /**
     * Display-boundary copy for `undoStudioCapabilityExecution`'s three
     * non-throwing failure returns (studio-capability-gateway.ts) — those
     * branches always populate `error` with one of three hardcoded English
     * literals, which made the localized `restoreFailure` fallback dead on
     * that path (final-review I5, wave-1 fix-round-2). The raw English
     * string still travels as secondary `detail`; only the primary line is
     * resolved through this table. A 4th non-throwing failure (the MCP bus
     * call itself failing) has no reason code here and keeps falling back
     * to `restoreFailure` — that message comes from the bus/tool layer, not
     * one of these three fixed literals, and stays out of this repair's
     * scope.
     */
    undoFailureReasons: Record<
      "executionNotFound" | "notLatestUndoable" | "undoStackChanged",
      string
    >;
    state: Record<"working" | "changed" | "attention" | "blocked" | "completed", string>;
  };
  commandDeck: {
    goal: string;
    goalMissing: string;
    currentCourse: string;
    describeNext: string;
    phase: Record<
      | "understanding"
      | "needs-input"
      | "ready"
      | "awaiting-confirmation"
      | "running"
      | "applied"
      | "verified"
      | "blocked"
      | "failed"
      | "undone",
      string
    >;
    course: Record<
      | "understanding"
      | "needs-input"
      | "ready"
      | "awaiting-confirmation"
      | "running"
      | "applied"
      | "verified"
      | "blocked"
      | "failed"
      | "undone",
      { message: string; nextAction: string }
    >;
    /**
     * The running course for a CHAT request, which is not the same fact as
     * `course.running`.
     *
     * `course.running` says Coogo is applying an APPROVED plan and verifying
     * it by readback — true of a confirmed capability execution, and false of
     * a message just sent to the agent runtime, which nobody approved and
     * which may come back as prose. Reusing it made the surface claim an
     * approval that had not happened, which is the same defect as the stale
     * phase one step along.
     */
    courseRequestInFlight: { message: string; nextAction: string };
    // C5 — creator-facing variant of course.applied.nextAction. The
    // developer-facing copy ("...review the execution evidence.") names an
    // internal artifact; the creator persona sees this plain-language
    // alternative instead. Only the "applied" phase is covered per the
    // audit finding — other phases are unaffected.
    appliedNextActionCreator: string;
    affectedOutputs: string;
    pendingImpact: (label: string, count: number) => string;
    changes: (count: number) => string;
    reviewAndApprove: string;
    reviewImpact: string;
    hideImpact: string;
    coordinatedApproval: string;
    preparedImpactSummary: (count: number) => string;
    changeItem: (index: number) => string;
    noImpact: string;
    approvalBlockedNoEvidence: string;
    before: string;
    after: string;
    typeToConfirm: (phrase: string) => string;
    /**
     * P2 (finding 3): the phrase-interpolating `typeToConfirm` buried an
     * ALL-CAPS English token (`RENAME HOME`, minted by
     * `step5ConfirmationPhrase()` from the verb plus the internal page id)
     * mid-sentence, where a Korean operator could not tell where the token
     * started or ended. This variant states the REQUIREMENT only; the
     * surface renders the phrase itself beside it, isolated and selectable,
     * so it is transcribable character-for-character.
     */
    typeToConfirmHint: string;
    approve: string;
    cancel: string;
    applying: string;
    cancelling: string;
    approvalFailed: string;
    cancellationFailed: string;
    approvalApplied: string;
    approvalCancelled: string;
    confirmationFailure: Record<
      | "CONFIRMATION_MISSING_OR_EXPIRED"
      | "CONFIRMATION_SCOPE_MISMATCH"
      | "CONFIRMATION_PHRASE_MISMATCH"
      | "CONFIRMATION_STALE"
      // wave-3 (Lane A) — studio-capability-gateway.ts's
      // StudioCapabilityFailureCode gained two new members closing the
      // post-confirmation write window
      // (docs/superpowers/specs/2026-08-17-studio-slice4-concurrent-write.md);
      // this Record must track them 1:1 or the indexed lookup in
      // studio-task-center.tsx's localizedApprovalFailure() fails to
      // type-check.
      | "CONFIRMATION_STALE_AT_COMMIT"
      | "CONFIRMATION_DOCUMENT_LOCKED"
      | "CONFIRMATION_IN_FLIGHT"
      | "DURABLE_JOURNAL_REQUIRED",
      string
    >;
  };
  nodeStatus: Record<StudioExecutionNodeStatus, string>;
  planStatus: Record<StudioExecutionPlanStatus, string>;
  source: Record<StudioExecutionSource, string>;
  approval: Record<StudioExecutionApprovalSource, string>;
  rollback: {
    notRequired: { label: string; detail: string };
    completed: { label: string; detail: string };
    noRetainedChange: { label: string; detail: string };
    failed: string;
    unavailable: string;
    failureFallback: string;
  };
  recovery: Record<
    | "failedRestored"
    | "failedUndo"
    | "failedUnavailable"
    | "undone"
    | "running"
    | "suspended"
    | "cancelled"
    | "completed",
    string
  >;
  timeline: {
    latestRecordedExecution: string;
    plan: string;
    steps: string;
    readback: string;
    rollback: string;
    captured: (count: number) => string;
    verified: (count: number) => string;
    notRecorded: string;
    stateCaptured: string;
    executionSteps: (label: string) => string;
    stepFallback: (index: number) => string;
    statusAt: (status: string, occurredAt: string | null) => string;
    readbackCaptured: string;
    readbackVerifiedNotRetained: string;
    readbackFailed: string;
    completedWithoutReadback: string;
    stepErrorFallback: string;
    noResult: string;
    authorityAndApproval: string;
    runStopped: string;
    nextStep: string;
    undoError: (error: string) => string;
    restoring: string;
    undoLastChange: string;
    technicalProvenance: string;
    provenancePlan: string;
    provenanceRun: string;
    provenanceChanges: string;
    exactPathsUnavailable: (tools: string) => string;
    provenanceProject: string;
    noProjectScope: string;
    provenanceRecorded: string;
    provenanceStateBefore: string;
    provenanceStateAfter: string;
  };
  /**
   * Display-boundary copy for `CoogoExecutionError.code`. Keyed by the closed
   * enum above plus `unknown` — a code missing from this record, or an error
   * with no code at all, must resolve to `unknown` rather than surface the
   * raw English `Error.message`. See `resolveStudioExecutionErrorCopy`.
   */
  errors: Record<StudioExecutionErrorCode | "unknown", string>;
}

const en: StudioExecutionCopy = {
  taskCenter: {
    restoreFailure: "Studio could not restore the previous state.",
    latestExecution: (count) =>
      `Latest execution · ${count} ${count === 1 ? "event" : "events"}`,
    loadingDetails: "Loading execution details…",
    attentionTitle: "Review this change",
    doneReviewing: "Done reviewing",
    continueInChat: "Continue in Chat",
    targetUnavailable: "The original target is no longer available",
    undoFailureReasons: {
      executionNotFound: "Capability execution not found.",
      notLatestUndoable:
        "Only the latest undoable capability execution can be reverted.",
      undoStackChanged:
        "The global undo stack changed after this capability execution. Undo the intervening action first.",
    },
    state: {
      working: "Working",
      changed: "Ready to review",
      attention: "Decision needed",
      blocked: "Blocked",
      completed: "Completed",
    },
  },
  commandDeck: {
    goal: "Goal",
    goalMissing: "Set the project goal in chat.",
    currentCourse: "Current course",
    describeNext: "Describe the result you want in chat.",
    phase: {
      understanding: "Understanding",
      "needs-input": "Input needed",
      ready: "Ready",
      "awaiting-confirmation": "Confirm change",
      running: "Working",
      applied: "Applied",
      verified: "Verified",
      blocked: "Blocked",
      failed: "Needs attention",
      undone: "Undone",
    },
    course: {
      understanding: {
        message: "Coogo is ready to turn the goal into a coordinated plan.",
        nextAction: "Describe the result you want in chat.",
      },
      "needs-input": {
        message: "Choose the output or source Coogo should work on.",
        nextAction: "Select a target in Studio or name it in chat.",
      },
      ready: {
        message: "The requested Studio operation is ready to prepare.",
        nextAction: "Describe the intended result and review the proposal.",
      },
      "awaiting-confirmation": {
        message:
          "A coordinated change plan is ready. Nothing changes until you approve it.",
        nextAction: "Review the exact impact, then approve or cancel.",
      },
      running: {
        message: "Coogo is applying the approved plan and checking the result.",
        nextAction: "Keep this task open until the check completes.",
      },
      applied: {
        message:
          "The latest coordinated plan was applied. Review the current outputs.",
        nextAction: "Continue in chat or review the execution evidence.",
      },
      verified: {
        message: "The check confirmed the latest coordinated change.",
        nextAction: "Continue in chat or review the retained evidence.",
      },
      blocked: {
        message: "The current course needs a decision or missing input.",
        nextAction: "Review the blocker before continuing.",
      },
      failed: {
        message: "The latest run needs attention before you continue.",
        nextAction: "Review the error and request a corrected proposal.",
      },
      undone: {
        message: "The latest applied change was undone.",
        nextAction: "Review the restored outputs or describe another change.",
      },
    },
    courseRequestInFlight: {
      message: "Coogo is working on your request.",
      nextAction: "Keep this open until the result arrives.",
    },
    appliedNextActionCreator: "Continue in chat or view what changed.",
    affectedOutputs: "Affected outputs",
    pendingImpact: (label, count) =>
      `${label}, ${count} ${count === 1 ? "change" : "changes"} awaiting approval`,
    changes: (count) =>
      `${count} planned ${count === 1 ? "change" : "changes"}`,
    reviewAndApprove: "Review and approve",
    reviewImpact: "Review impact",
    hideImpact: "Hide impact",
    coordinatedApproval: "Approval applies to this coordinated plan.",
    preparedImpactSummary: (count) =>
      `${count} exact ${count === 1 ? "change is" : "changes are"} prepared for review.`,
    changeItem: (index) => `Change ${index}`,
    noImpact: "No output changes are prepared.",
    approvalBlockedNoEvidence:
      "Approval is blocked because this proposal has no exact change paths. Cancel it and request a fresh proposal.",
    before: "Before",
    after: "After",
    typeToConfirm: (phrase) => `Type ${phrase} to continue`,
    typeToConfirmHint: "Type this exact phrase in the box below to continue:",
    approve: "Approve plan",
    cancel: "Cancel",
    applying: "Applying…",
    cancelling: "Cancelling…",
    approvalFailed: "Studio could not apply this approved plan.",
    cancellationFailed: "This approval is no longer available.",
    approvalApplied: "The approved plan was applied and sent for checking.",
    approvalCancelled: "The proposal was cancelled. Nothing was applied.",
    confirmationFailure: {
      CONFIRMATION_MISSING_OR_EXPIRED:
        "This approval is missing or expired. Request a fresh proposal.",
      CONFIRMATION_SCOPE_MISMATCH:
        "This approval belongs to another project or Studio session.",
      CONFIRMATION_PHRASE_MISMATCH:
        "The required confirmation phrase does not match.",
      CONFIRMATION_STALE:
        "This proposal is stale. Request a fresh proposal and review it again.",
      CONFIRMATION_STALE_AT_COMMIT:
        "This change could not be applied because the document changed right before it was written. Nothing was applied. Request a fresh proposal and review it again.",
      CONFIRMATION_DOCUMENT_LOCKED:
        "Another approved change is currently being applied to this document. Wait for it to finish, then confirm again.",
      CONFIRMATION_IN_FLIGHT: "This proposal is already being applied.",
      DURABLE_JOURNAL_REQUIRED:
        "Sign in again before approving this durable operation.",
    },
  },
  nodeStatus: {
    pending: "Not reached",
    running: "In progress",
    completed: "Completed",
    failed: "Failed",
  },
  planStatus: {
    executed: "Completed",
    failed: "Needs attention",
    undone: "Reverted",
    running: "In progress",
    suspended: "Suspended",
    cancelled: "Cancelled",
  },
  source: {
    "chat-intent-chunk": "Chat request",
    "chat-operation": "Chat operation",
    webmcp: "WebMCP",
    "studio-ui": "Studio",
  },
  approval: {
    "not-required": "Not required",
    "awaiting-user-approval": "Awaiting user approval",
    "not-approved": "Not approved",
    "studio-user-action": "Approved in Studio",
    "webmcp-user-approval": "Approved through WebMCP",
  },
  rollback: {
    notRequired: {
      label: "Not required",
      detail: "The run did not require automatic rollback.",
    },
    completed: {
      label: "Completed",
      detail: "Studio restored the state captured before this run.",
    },
    noRetainedChange: {
      label: "No retained change",
      detail: "The failed run left no retained change to restore.",
    },
    failed: "Failed",
    unavailable: "Unavailable",
    failureFallback:
      "Studio could not restore the previous state automatically.",
  },
  recovery: {
    failedRestored:
      "The previous state is safe. Review the failed node, correct the request or input, then start a new run.",
    failedUndo:
      "Automatic recovery did not complete. Use Undo below to restore the last retained Studio change before retrying.",
    failedUnavailable:
      "Automatic recovery did not complete and this run cannot be resumed. Review the error before starting a corrected request.",
    undone:
      "This run was reverted. Start a new request when you are ready to apply a corrected change.",
    running:
      "This run was recovered from the durable journal. Review the last recorded node before continuing or cancelling it.",
    suspended:
      "This run is suspended. Review its authority and recorded state before making an explicit continuation decision.",
    cancelled:
      "This run was cancelled. Start a new request if you still want to apply the change.",
    completed:
      "This run is complete. Completed runs cannot be paused or resumed; start a new request for another change.",
  },
  timeline: {
    latestRecordedExecution: "Latest recorded execution",
    plan: "Plan",
    steps: "Steps",
    readback: "Verification",
    rollback: "Rollback",
    captured: (count) => `${count} captured`,
    verified: (count) => `${count} verified`,
    notRecorded: "Not recorded",
    stateCaptured: "State captured",
    executionSteps: (label) => `Execution steps for ${label}`,
    stepFallback: (index) => `Step ${index}`,
    statusAt: (status, occurredAt) =>
      occurredAt ? `${status} at ${occurredAt}` : status,
    readbackCaptured: "Verification evidence captured.",
    readbackVerifiedNotRetained:
      "The check succeeded, but its evidence was not kept in the permanent record.",
    readbackFailed:
      "The check failed, so this step has no confirmed evidence.",
    completedWithoutReadback: "Completed without a separate check.",
    stepErrorFallback: "The step returned an error.",
    noResult: "No result was recorded for this step.",
    authorityAndApproval: "Authority and approval",
    runStopped: "The run stopped before completion",
    nextStep: "Next step",
    undoError: (error) => `Undo did not complete: ${error}`,
    restoring: "Restoring previous state",
    undoLastChange: "Undo last change",
    technicalProvenance: "Technical provenance",
    provenancePlan: "Plan",
    provenanceRun: "Run",
    provenanceChanges: "Changes",
    exactPathsUnavailable: (tools) => `Exact paths unavailable · ${tools}`,
    provenanceProject: "Project",
    noProjectScope: "No project scope recorded",
    provenanceRecorded: "Recorded",
    provenanceStateBefore: "State before",
    provenanceStateAfter: "State after",
  },
  errors: {
    checkpoint_authority_mismatch:
      "Studio could not confirm this change against its recorded checkpoint. Request a fresh proposal.",
    CONFIRMATION_STALE:
      "This proposal is stale. Request a fresh proposal and review it again.",
    durable_orchestration_auth_required:
      "Sign in again to continue this durable operation.",
    exact_impact_invalid:
      "Studio received an invalid change record and could not continue.",
    exact_impact_unavailable:
      "Studio could not read the exact change record for this step.",
    execution_lease_active:
      "This Studio session already holds a lock on this run.",
    execution_lease_lost:
      "Studio lost its execution lock on this run. Request a fresh proposal.",
    handler_not_registered: "Studio does not support this operation.",
    invalid_durable_event:
      "Studio found an invalid event in this run's history.",
    invalid_durable_plan:
      "Studio found an invalid recorded plan for this run.",
    invalid_transition: "Studio rejected an invalid step in this run.",
    journal_checkpoint_inconsistent:
      "Studio's recorded run history is inconsistent.",
    journal_receipt_mismatch:
      "Studio's recorded confirmation does not match this run.",
    journal_replay_receipt_required:
      "Studio could not confirm a retried step was recorded exactly once.",
    journal_run_mismatch: "This record does not belong to the current run.",
    lease_receipt_mismatch:
      "Studio's execution lock confirmation does not match this run.",
    manual_recovery_required:
      "Studio could not automatically recover this run. Manual review is required.",
    mutation_outcome_evidence_missing:
      "Studio is missing the evidence needed to confirm this change's outcome.",
    mutation_outcome_not_unknown:
      "This run's outcome is already determined and cannot be reclassified.",
    resume_authority_invalid:
      "Studio could not find the approval needed to resume this run.",
    resume_authority_rejected:
      "Studio rejected the approval to resume this run.",
    resume_grant_binding_mismatch:
      "This resume approval does not match the current run.",
    resume_grant_checkpoint_mismatch:
      "This resume approval does not match the run's recorded checkpoint.",
    studio_tool_failed: "Studio could not complete this change.",
    undo_ownership_missing:
      "Studio did not receive the authority needed to undo this change.",
    unsafe_transition_material:
      "Studio blocked a step that would have exposed sensitive information.",
    unknown: "Studio ran into an unexpected problem. Try again or request a fresh proposal.",
  },
};

const ko: StudioExecutionCopy = {
  taskCenter: {
    restoreFailure: "Studio가 이전 상태를 복원하지 못했습니다.",
    latestExecution: (count) => `최근 실행 · 이벤트 ${count}개`,
    loadingDetails: "실행 세부 정보를 불러오는 중…",
    attentionTitle: "이 변경 확인",
    doneReviewing: "확인 완료",
    continueInChat: "채팅에서 계속",
    targetUnavailable: "원래 대상이 더 이상 존재하지 않습니다",
    undoFailureReasons: {
      executionNotFound: "실행 기록을 찾을 수 없습니다.",
      notLatestUndoable:
        "가장 최근에 실행되어 되돌리기 가능한 작업만 되돌릴 수 있습니다.",
      undoStackChanged:
        "이 실행 이후 전역 되돌리기 기록이 변경되었습니다. 먼저 중간에 실행된 작업을 되돌리세요.",
    },
    state: {
      working: "진행 중",
      changed: "확인 준비됨",
      attention: "결정 필요",
      blocked: "차단됨",
      completed: "완료됨",
    },
  },
  commandDeck: {
    goal: "목표",
    goalMissing: "채팅에서 프로젝트 목표를 정하세요.",
    currentCourse: "현재 진행",
    describeNext: "채팅에서 원하는 결과를 설명하세요.",
    phase: {
      understanding: "요청 이해 중",
      "needs-input": "입력 필요",
      ready: "준비됨",
      "awaiting-confirmation": "변경 승인",
      running: "진행 중",
      applied: "적용됨",
      verified: "검증됨",
      blocked: "차단됨",
      failed: "확인 필요",
      undone: "되돌림",
    },
    course: {
      understanding: {
        message: "Coogo가 목표를 조정된 실행 계획으로 만들 준비가 됐습니다.",
        nextAction: "채팅에서 원하는 결과를 설명하세요.",
      },
      "needs-input": {
        message: "Coogo가 작업할 결과물이나 자료를 선택해야 합니다.",
        nextAction: "Studio에서 대상을 선택하거나 채팅에 이름을 입력하세요.",
      },
      ready: {
        message: "요청한 Studio 작업을 계획으로 준비할 수 있습니다.",
        nextAction: "원하는 결과를 설명하고 제안을 검토하세요.",
      },
      "awaiting-confirmation": {
        message:
          "조정된 변경 계획이 준비됐습니다. 승인하기 전에는 아무것도 바뀌지 않습니다.",
        nextAction: "정확한 영향을 검토한 뒤 승인하거나 취소하세요.",
      },
      running: {
        message: "Coogo가 승인된 계획을 적용하고 결과를 확인하고 있습니다.",
        nextAction: "결과 확인이 끝날 때까지 이 작업을 열어 두세요.",
      },
      applied: {
        message: "최근 조정 계획을 적용했습니다. 현재 결과물을 확인하세요.",
        nextAction: "채팅을 계속하거나 실행 근거를 검토하세요.",
      },
      verified: {
        message: "결과 읽기 검증으로 최근 변경을 확인했습니다.",
        nextAction: "채팅을 계속하거나 보존된 근거를 검토하세요.",
      },
      blocked: {
        message: "현재 진행에는 결정이나 누락된 입력이 필요합니다.",
        nextAction: "계속하기 전에 차단 원인을 확인하세요.",
      },
      failed: {
        message: "계속하기 전에 최근 실행 결과를 확인해야 합니다.",
        nextAction: "오류를 확인하고 수정된 제안을 요청하세요.",
      },
      undone: {
        message: "최근 적용된 변경을 되돌렸습니다.",
        nextAction: "복원된 결과물을 확인하거나 다른 변경을 요청하세요.",
      },
    },
    courseRequestInFlight: {
      message: "Coogo가 요청을 처리하고 있습니다.",
      nextAction: "결과가 도착할 때까지 이 작업을 열어 두세요.",
    },
    appliedNextActionCreator: "채팅으로 계속하거나 변경 내역을 확인하세요.",
    affectedOutputs: "영향받는 결과물",
    pendingImpact: (label, count) => `${label}, 변경 ${count}개 승인 대기`,
    changes: (count) => `예정된 변경 ${count}개`,
    reviewAndApprove: "영향 검토 및 승인",
    reviewImpact: "영향 검토",
    hideImpact: "영향 접기",
    coordinatedApproval: "승인은 이 조정된 변경 계획 전체에 적용됩니다.",
    preparedImpactSummary: (count) =>
      `검토할 정확한 변경 ${count}개가 준비됐습니다.`,
    changeItem: (index) => `변경 ${index}`,
    noImpact: "준비된 결과물 변경이 없습니다.",
    approvalBlockedNoEvidence:
      "정확한 변경 경로가 없어 승인할 수 없습니다. 이 제안을 취소하고 새 제안을 요청하세요.",
    before: "변경 전",
    after: "변경 후",
    typeToConfirm: (phrase) => `계속하려면 ${phrase} 입력`,
    typeToConfirmHint: "계속하려면 아래 칸에 다음 문구를 그대로 입력하세요:",
    approve: "계획 승인",
    cancel: "취소",
    applying: "적용 중…",
    cancelling: "취소 중…",
    approvalFailed: "Studio가 승인된 계획을 적용하지 못했습니다.",
    cancellationFailed: "이 승인은 더 이상 사용할 수 없습니다.",
    approvalApplied: "승인된 계획을 적용하고 결과 확인을 시작했습니다.",
    approvalCancelled: "제안을 취소했습니다. 적용된 변경은 없습니다.",
    confirmationFailure: {
      CONFIRMATION_MISSING_OR_EXPIRED:
        "승인이 없거나 만료되었습니다. 새 제안을 요청하세요.",
      CONFIRMATION_SCOPE_MISMATCH:
        "이 승인은 다른 프로젝트 또는 Studio 세션에 속합니다.",
      CONFIRMATION_PHRASE_MISMATCH: "필수 확인 문구가 일치하지 않습니다.",
      CONFIRMATION_STALE:
        "제안이 현재 상태와 일치하지 않습니다. 새 제안을 요청하고 다시 검토하세요.",
      CONFIRMATION_STALE_AT_COMMIT:
        "적용되기 직전에 문서가 변경되어 이 변경을 적용하지 못했습니다. 아무것도 적용되지 않았습니다. 새 제안을 요청하고 다시 검토하세요.",
      CONFIRMATION_DOCUMENT_LOCKED:
        "다른 승인된 변경이 현재 이 문서에 적용되고 있습니다. 완료될 때까지 기다린 후 다시 승인하세요.",
      CONFIRMATION_IN_FLIGHT: "이 제안은 이미 적용 중입니다.",
      DURABLE_JOURNAL_REQUIRED: "이 작업을 승인하려면 다시 로그인해야 합니다.",
    },
  },
  nodeStatus: {
    pending: "미실행",
    running: "진행 중",
    completed: "완료",
    failed: "실패",
  },
  planStatus: {
    executed: "완료",
    failed: "확인 필요",
    undone: "되돌림",
    running: "진행 중",
    suspended: "일시 중단",
    cancelled: "취소됨",
  },
  source: {
    "chat-intent-chunk": "채팅 요청",
    "chat-operation": "채팅 작업",
    webmcp: "WebMCP",
    "studio-ui": "Studio",
  },
  approval: {
    "not-required": "불필요",
    "awaiting-user-approval": "사용자 승인 대기",
    "not-approved": "승인되지 않음",
    "studio-user-action": "Studio에서 승인",
    "webmcp-user-approval": "WebMCP에서 승인",
  },
  rollback: {
    notRequired: {
      label: "불필요",
      detail: "이 실행에는 자동 롤백이 필요하지 않았습니다.",
    },
    completed: {
      label: "완료",
      detail: "Studio가 실행 전 상태를 복원했습니다.",
    },
    noRetainedChange: {
      label: "유지된 변경 없음",
      detail: "실패한 실행이 복원할 변경을 남기지 않았습니다.",
    },
    failed: "실패",
    unavailable: "사용할 수 없음",
    failureFallback: "Studio가 이전 상태를 자동으로 복원하지 못했습니다.",
  },
  recovery: {
    failedRestored:
      "이전 상태는 안전합니다. 실패한 노드를 확인하고 요청이나 입력을 수정한 다음 새 실행을 시작하세요.",
    failedUndo:
      "자동 복구가 완료되지 않았습니다. 다시 시도하기 전에 아래의 되돌리기로 마지막 변경을 복원하세요.",
    failedUnavailable:
      "자동 복구가 완료되지 않았고 이 실행은 재개할 수 없습니다. 오류를 확인한 뒤 수정된 요청을 시작하세요.",
    undone:
      "이 실행은 되돌렸습니다. 수정된 변경을 적용하려면 새 요청을 시작하세요.",
    running:
      "지속 저널에서 실행을 복구했습니다. 계속하거나 취소하기 전에 마지막 기록 노드를 확인하세요.",
    suspended:
      "실행이 일시 중단되었습니다. 명시적으로 계속할지 결정하기 전에 권한과 기록 상태를 확인하세요.",
    cancelled:
      "실행이 취소되었습니다. 변경이 여전히 필요하면 새 요청을 시작하세요.",
    completed:
      "실행이 완료되었습니다. 완료된 실행은 일시 중지하거나 재개할 수 없으므로 다른 변경은 새 요청으로 시작하세요.",
  },
  timeline: {
    latestRecordedExecution: "최근 기록된 실행",
    plan: "계획",
    steps: "단계",
    readback: "검증",
    rollback: "롤백",
    captured: (count) => `${count}개 보존`,
    verified: (count) => `${count}개 검증`,
    notRecorded: "기록되지 않음",
    stateCaptured: "상태 보존됨",
    executionSteps: (label) => `${label} 실행 단계`,
    stepFallback: (index) => `${index}단계`,
    statusAt: (status, occurredAt) =>
      occurredAt ? `${status} · ${occurredAt}` : status,
    readbackCaptured: "검증 근거를 보존했습니다.",
    readbackVerifiedNotRetained:
      "검증은 성공했지만 근거가 영구 기록에 남지 않았습니다.",
    readbackFailed:
      "검증이 실패하여 이 단계에는 확인된 근거가 없습니다.",
    completedWithoutReadback: "별도 검증 없이 완료되었습니다.",
    stepErrorFallback: "단계에서 오류를 반환했습니다.",
    noResult: "이 단계에 기록된 결과가 없습니다.",
    authorityAndApproval: "권한 및 승인",
    runStopped: "완료 전에 실행이 중단되었습니다",
    nextStep: "다음 단계",
    undoError: (error) => `되돌리기를 완료하지 못했습니다: ${error}`,
    restoring: "이전 상태 복원 중",
    undoLastChange: "마지막 변경 되돌리기",
    technicalProvenance: "기술 출처 정보",
    provenancePlan: "계획",
    provenanceRun: "실행",
    provenanceChanges: "변경",
    exactPathsUnavailable: (tools) => `정확한 경로 없음 · ${tools}`,
    provenanceProject: "프로젝트",
    noProjectScope: "기록된 프로젝트 범위 없음",
    provenanceRecorded: "기록 시각",
    provenanceStateBefore: "변경 전 상태",
    provenanceStateAfter: "변경 후 상태",
  },
  errors: {
    checkpoint_authority_mismatch:
      "Studio가 기록된 검사점과 이 변경을 확인하지 못했습니다. 새 제안을 요청하세요.",
    CONFIRMATION_STALE:
      "제안이 현재 상태와 일치하지 않습니다. 새 제안을 요청하고 다시 검토하세요.",
    durable_orchestration_auth_required: "이 작업을 계속하려면 다시 로그인하세요.",
    exact_impact_invalid:
      "Studio가 잘못된 변경 기록을 받아 계속할 수 없습니다.",
    exact_impact_unavailable:
      "Studio가 이 단계의 정확한 변경 기록을 읽지 못했습니다.",
    execution_lease_active:
      "이 Studio 세션이 이미 이 실행에 대한 잠금을 보유하고 있습니다.",
    execution_lease_lost:
      "Studio가 이 실행에 대한 실행 잠금을 잃었습니다. 새 제안을 요청하세요.",
    handler_not_registered: "Studio는 이 작업을 지원하지 않습니다.",
    invalid_durable_event:
      "Studio가 이 실행 기록에서 잘못된 이벤트를 발견했습니다.",
    invalid_durable_plan:
      "Studio가 이 실행에 대해 잘못된 기록된 계획을 발견했습니다.",
    invalid_transition: "Studio가 이 실행에서 잘못된 단계를 거부했습니다.",
    journal_checkpoint_inconsistent:
      "Studio의 기록된 실행 이력이 일치하지 않습니다.",
    journal_receipt_mismatch:
      "Studio의 기록된 확인이 이 실행과 일치하지 않습니다.",
    journal_replay_receipt_required:
      "Studio가 재시도된 단계가 정확히 한 번 기록됐는지 확인하지 못했습니다.",
    journal_run_mismatch: "이 기록은 현재 실행에 속하지 않습니다.",
    lease_receipt_mismatch:
      "Studio의 실행 잠금 확인이 이 실행과 일치하지 않습니다.",
    manual_recovery_required:
      "Studio가 이 실행을 자동으로 복구하지 못했습니다. 수동 검토가 필요합니다.",
    mutation_outcome_evidence_missing:
      "이 변경 결과를 확인하는 데 필요한 근거가 Studio에 없습니다.",
    mutation_outcome_not_unknown:
      "이 실행의 결과는 이미 확인되어 다시 분류할 수 없습니다.",
    resume_authority_invalid:
      "Studio가 이 실행을 재개하는 데 필요한 승인을 찾지 못했습니다.",
    resume_authority_rejected: "Studio가 이 실행을 재개하는 승인을 거부했습니다.",
    resume_grant_binding_mismatch: "이 재개 승인이 현재 실행과 일치하지 않습니다.",
    resume_grant_checkpoint_mismatch:
      "이 재개 승인이 실행의 기록된 검사점과 일치하지 않습니다.",
    studio_tool_failed: "Studio가 이 변경을 완료하지 못했습니다.",
    undo_ownership_missing:
      "Studio가 이 변경을 되돌리는 데 필요한 권한을 받지 못했습니다.",
    unsafe_transition_material:
      "Studio가 민감한 정보를 노출할 수 있는 단계를 차단했습니다.",
    unknown: "Studio에 예기치 않은 문제가 발생했습니다. 다시 시도하거나 새 제안을 요청하세요.",
  },
};

const ja: StudioExecutionCopy = {
  taskCenter: {
    restoreFailure: "Studioは以前の状態を復元できませんでした。",
    latestExecution: (count) => `最新の実行 · ${count}件のイベント`,
    loadingDetails: "実行の詳細を読み込み中…",
    attentionTitle: "この変更を確認",
    doneReviewing: "確認完了",
    continueInChat: "チャットで続ける",
    targetUnavailable: "元の対象は利用できなくなりました",
    undoFailureReasons: {
      executionNotFound: "実行記録が見つかりません。",
      notLatestUndoable: "取り消せるのは最新の実行のみです。",
      undoStackChanged:
        "この実行後にグローバルな取り消し履歴が変更されました。先に間に入った操作を取り消してください。",
    },
    state: {
      working: "進行中",
      changed: "確認待ち",
      attention: "判断が必要",
      blocked: "ブロック中",
      completed: "完了",
    },
  },
  commandDeck: {
    goal: "目標",
    goalMissing: "チャットでプロジェクト目標を設定してください。",
    currentCourse: "現在の進行",
    describeNext: "チャットで希望する結果を説明してください。",
    phase: {
      understanding: "確認中",
      "needs-input": "入力が必要",
      ready: "準備完了",
      "awaiting-confirmation": "変更を承認",
      running: "進行中",
      applied: "適用済み",
      verified: "検証済み",
      blocked: "ブロック中",
      failed: "要確認",
      undone: "元に戻しました",
    },
    course: {
      understanding: {
        message: "Coogoは目標を連携された実行プランにする準備ができています。",
        nextAction: "チャットで希望する結果を説明してください。",
      },
      "needs-input": {
        message: "Coogoが作業する成果物またはソースを選択してください。",
        nextAction: "Studioで対象を選ぶか、チャットで名前を指定してください。",
      },
      ready: {
        message: "指定されたStudio操作をプランとして準備できます。",
        nextAction: "希望する結果を説明し、提案を確認してください。",
      },
      "awaiting-confirmation": {
        message:
          "連携された変更プランが準備できました。承認するまで変更は適用されません。",
        nextAction:
          "正確な影響を確認してから、承認またはキャンセルしてください。",
      },
      running: {
        message: "Coogoが承認済みプランを適用し、結果を確認しています。",
        nextAction: "確認が完了するまでこのタスクを開いたままにしてください。",
      },
      applied: {
        message:
          "最新の連携プランを適用しました。現在の成果物を確認してください。",
        nextAction: "チャットを続けるか、実行証跡を確認してください。",
      },
      verified: {
        message: "検証により最新の変更を確認しました。",
        nextAction: "チャットを続けるか、保持された証跡を確認してください。",
      },
      blocked: {
        message: "現在の進行には判断または不足している入力が必要です。",
        nextAction: "続行する前にブロック理由を確認してください。",
      },
      failed: {
        message: "続行する前に最新の実行結果を確認する必要があります。",
        nextAction: "エラーを確認し、修正された提案を依頼してください。",
      },
      undone: {
        message: "直近に適用された変更を元に戻しました。",
        nextAction:
          "復元された成果物を確認するか、別の変更を依頼してください。",
      },
    },
    courseRequestInFlight: {
      message: "Coogo がリクエストを処理しています。",
      nextAction: "結果が届くまでこの作業を開いたままにしてください。",
    },
    appliedNextActionCreator:
      "チャットを続けるか、変更内容を確認してください。",
    affectedOutputs: "影響を受ける成果物",
    pendingImpact: (label, count) => `${label}、${count}件の変更が承認待ち`,
    changes: (count) => `予定されている変更 ${count}件`,
    reviewAndApprove: "影響を確認して承認",
    reviewImpact: "影響を確認",
    hideImpact: "影響を閉じる",
    coordinatedApproval: "承認はこの連携された変更プラン全体に適用されます。",
    preparedImpactSummary: (count) =>
      `確認できる正確な変更が${count}件準備されています。`,
    changeItem: (index) => `変更 ${index}`,
    noImpact: "準備された成果物の変更はありません。",
    approvalBlockedNoEvidence:
      "正確な変更パスがないため承認できません。この提案をキャンセルし、新しい提案を依頼してください。",
    before: "変更前",
    after: "変更後",
    typeToConfirm: (phrase) => `続行するには${phrase}と入力`,
    typeToConfirmHint: "続けるには、下の欄に次の語句をそのまま入力してください:",
    approve: "プランを承認",
    cancel: "キャンセル",
    applying: "適用中…",
    cancelling: "キャンセル中…",
    approvalFailed: "Studioは承認済みプランを適用できませんでした。",
    cancellationFailed: "この承認は利用できなくなりました。",
    approvalApplied: "承認済みプランを適用し、結果の確認を開始しました。",
    approvalCancelled: "提案をキャンセルしました。変更は適用されていません。",
    confirmationFailure: {
      CONFIRMATION_MISSING_OR_EXPIRED:
        "承認が見つからないか期限切れです。新しい提案を依頼してください。",
      CONFIRMATION_SCOPE_MISMATCH:
        "この承認は別のプロジェクトまたはStudioセッションに属しています。",
      CONFIRMATION_PHRASE_MISMATCH: "必要な確認フレーズが一致しません。",
      CONFIRMATION_STALE:
        "提案が現在の状態と一致しません。新しい提案を依頼して再確認してください。",
      CONFIRMATION_STALE_AT_COMMIT:
        "書き込み直前にドキュメントが変更されたため、この変更を適用できませんでした。何も適用されていません。新しい提案を依頼して再確認してください。",
      CONFIRMATION_DOCUMENT_LOCKED:
        "別の承認済みの変更が現在このドキュメントに適用中です。完了するまで待ってから、もう一度承認してください。",
      CONFIRMATION_IN_FLIGHT: "この提案はすでに適用中です。",
      DURABLE_JOURNAL_REQUIRED:
        "この操作を承認するには、もう一度サインインしてください。",
    },
  },
  nodeStatus: {
    pending: "未到達",
    running: "進行中",
    completed: "完了",
    failed: "失敗",
  },
  planStatus: {
    executed: "完了",
    failed: "要確認",
    undone: "元に戻しました",
    running: "進行中",
    suspended: "一時停止",
    cancelled: "キャンセル済み",
  },
  source: {
    "chat-intent-chunk": "チャットリクエスト",
    "chat-operation": "チャット操作",
    webmcp: "WebMCP",
    "studio-ui": "Studio",
  },
  approval: {
    "not-required": "不要",
    "awaiting-user-approval": "ユーザー承認待ち",
    "not-approved": "未承認",
    "studio-user-action": "Studioで承認",
    "webmcp-user-approval": "WebMCPで承認",
  },
  rollback: {
    notRequired: {
      label: "不要",
      detail: "この実行に自動ロールバックは不要でした。",
    },
    completed: {
      label: "完了",
      detail: "Studioは実行前の状態を復元しました。",
    },
    noRetainedChange: {
      label: "保持された変更なし",
      detail: "失敗した実行には復元対象の変更が残りませんでした。",
    },
    failed: "失敗",
    unavailable: "利用不可",
    failureFallback: "Studioは以前の状態を自動復元できませんでした。",
  },
  recovery: {
    failedRestored:
      "以前の状態は安全です。失敗したノードを確認し、リクエストまたは入力を修正して新しい実行を開始してください。",
    failedUndo:
      "自動復旧は完了していません。再試行前に下の取り消しで最後の変更を復元してください。",
    failedUnavailable:
      "自動復旧は完了せず、この実行は再開できません。エラーを確認して修正済みのリクエストを開始してください。",
    undone:
      "この実行は元に戻しました。修正した変更は新しいリクエストで適用してください。",
    running:
      "永続ジャーナルから実行を復旧しました。続行またはキャンセルする前に最後の記録ノードを確認してください。",
    suspended:
      "実行は一時停止中です。明示的に続行を決める前に権限と記録状態を確認してください。",
    cancelled:
      "実行はキャンセルされました。変更が必要な場合は新しいリクエストを開始してください。",
    completed:
      "実行は完了しました。完了した実行は一時停止や再開ができないため、別の変更は新しいリクエストで開始してください。",
  },
  timeline: {
    latestRecordedExecution: "最新の記録済み実行",
    plan: "プラン",
    steps: "ステップ",
    readback: "検証",
    rollback: "ロールバック",
    captured: (count) => `${count}件を保持`,
    verified: (count) => `${count}件を検証`,
    notRecorded: "未記録",
    stateCaptured: "状態を保持済み",
    executionSteps: (label) => `${label}の実行ステップ`,
    stepFallback: (index) => `ステップ${index}`,
    statusAt: (status, occurredAt) =>
      occurredAt ? `${status} · ${occurredAt}` : status,
    readbackCaptured: "検証の根拠を保持しました。",
    readbackVerifiedNotRetained:
      "検証は成功しましたが、根拠は永続的な記録に保持されていません。",
    readbackFailed:
      "検証に失敗したため、このステップに確認された根拠はありません。",
    completedWithoutReadback: "個別の検証なしで完了しました。",
    stepErrorFallback: "ステップがエラーを返しました。",
    noResult: "このステップの結果は記録されていません。",
    authorityAndApproval: "権限と承認",
    runStopped: "完了前に実行が停止しました",
    nextStep: "次のステップ",
    undoError: (error) => `取り消しを完了できませんでした: ${error}`,
    restoring: "以前の状態を復元中",
    undoLastChange: "最後の変更を取り消す",
    technicalProvenance: "技術的な出所情報",
    provenancePlan: "プラン",
    provenanceRun: "実行",
    provenanceChanges: "変更",
    exactPathsUnavailable: (tools) => `正確なパスは未記録 · ${tools}`,
    provenanceProject: "プロジェクト",
    noProjectScope: "プロジェクト範囲は未記録",
    provenanceRecorded: "記録日時",
    provenanceStateBefore: "変更前の状態",
    provenanceStateAfter: "変更後の状態",
  },
  errors: {
    checkpoint_authority_mismatch:
      "Studioは記録済みのチェックポイントとこの変更を確認できませんでした。新しい提案を依頼してください。",
    CONFIRMATION_STALE:
      "提案が現在の状態と一致しません。新しい提案を依頼して再確認してください。",
    durable_orchestration_auth_required:
      "この操作を続けるには、もう一度サインインしてください。",
    exact_impact_invalid:
      "Studioは無効な変更記録を受け取ったため続行できませんでした。",
    exact_impact_unavailable:
      "Studioはこのステップの正確な変更記録を読み取れませんでした。",
    execution_lease_active:
      "このStudioセッションはすでにこの実行のロックを保持しています。",
    execution_lease_lost:
      "Studioはこの実行の実行ロックを失いました。新しい提案を依頼してください。",
    handler_not_registered: "Studioはこの操作をサポートしていません。",
    invalid_durable_event:
      "Studioはこの実行履歴で無効なイベントを検出しました。",
    invalid_durable_plan:
      "Studioはこの実行に対して無効な記録済みプランを検出しました。",
    invalid_transition: "Studioはこの実行で無効なステップを拒否しました。",
    journal_checkpoint_inconsistent:
      "Studioの記録済み実行履歴に矛盾があります。",
    journal_receipt_mismatch:
      "Studioの記録済み確認がこの実行と一致しません。",
    journal_replay_receipt_required:
      "Studioは再試行されたステップが正確に一度だけ記録されたことを確認できませんでした。",
    journal_run_mismatch: "この記録は現在の実行に属していません。",
    lease_receipt_mismatch:
      "Studioの実行ロック確認がこの実行と一致しません。",
    manual_recovery_required:
      "Studioはこの実行を自動的に復旧できませんでした。手動での確認が必要です。",
    mutation_outcome_evidence_missing:
      "この変更結果を確認するために必要な根拠がStudioにありません。",
    mutation_outcome_not_unknown:
      "この実行の結果はすでに確認済みのため、再分類できません。",
    resume_authority_invalid:
      "Studioはこの実行を再開するために必要な承認を見つけられませんでした。",
    resume_authority_rejected: "Studioはこの実行を再開する承認を拒否しました。",
    resume_grant_binding_mismatch: "この再開承認は現在の実行と一致しません。",
    resume_grant_checkpoint_mismatch:
      "この再開承認は実行の記録済みチェックポイントと一致しません。",
    studio_tool_failed: "Studioはこの変更を完了できませんでした。",
    undo_ownership_missing:
      "Studioはこの変更を元に戻すために必要な権限を受け取れませんでした。",
    unsafe_transition_material:
      "Studioは機密情報を露出する可能性のあるステップをブロックしました。",
    unknown:
      "Studioで予期しない問題が発生しました。もう一度試すか、新しい提案を依頼してください。",
  },
};

const zh: StudioExecutionCopy = {
  taskCenter: {
    restoreFailure: "Studio无法恢复之前的状态。",
    latestExecution: (count) => `最近执行 · ${count}个事件`,
    loadingDetails: "正在加载执行详情…",
    attentionTitle: "查看此更改",
    doneReviewing: "完成查看",
    continueInChat: "在聊天中继续",
    targetUnavailable: "原始目标已不可用",
    undoFailureReasons: {
      executionNotFound: "未找到该执行记录。",
      notLatestUndoable: "只能撤销最近一次可撤销的执行。",
      undoStackChanged:
        "此次执行后全局撤销记录已发生变化。请先撤销中间的操作。",
    },
    state: {
      working: "处理中",
      changed: "可供查看",
      attention: "需要决定",
      blocked: "已阻止",
      completed: "已完成",
    },
  },
  commandDeck: {
    goal: "目标",
    goalMissing: "请在聊天中设定项目目标。",
    currentCourse: "当前进度",
    describeNext: "请在聊天中说明期望的结果。",
    phase: {
      understanding: "正在理解",
      "needs-input": "需要输入",
      ready: "已就绪",
      "awaiting-confirmation": "审批变更",
      running: "进行中",
      applied: "已应用",
      verified: "已验证",
      blocked: "已阻止",
      failed: "需要处理",
      undone: "已撤销",
    },
    course: {
      understanding: {
        message: "Coogo已准备好把目标整理为协同执行计划。",
        nextAction: "请在聊天中说明期望的结果。",
      },
      "needs-input": {
        message: "请选择Coogo需要处理的成果或来源。",
        nextAction: "在Studio中选择目标，或在聊天中指明名称。",
      },
      ready: {
        message: "所请求的Studio操作可以生成计划。",
        nextAction: "说明期望结果并查看提案。",
      },
      "awaiting-confirmation": {
        message: "协同变更计划已准备好。未经审批不会应用任何变更。",
        nextAction: "查看准确影响后批准或取消。",
      },
      running: {
        message: "Coogo正在应用已批准的计划并核对结果。",
        nextAction: "请保持此任务开启，直到回读完成。",
      },
      applied: {
        message: "最近的协同计划已应用。请检查当前成果。",
        nextAction: "继续聊天或查看执行证据。",
      },
      verified: {
        message: "回读已验证最近的协同变更。",
        nextAction: "继续聊天或查看保留的证据。",
      },
      blocked: {
        message: "当前进度需要决策或缺失的输入。",
        nextAction: "继续前请查看阻塞原因。",
      },
      failed: {
        message: "继续前需要处理最近一次运行的结果。",
        nextAction: "查看错误并请求修正后的提案。",
      },
      undone: {
        message: "最近应用的变更已撤销。",
        nextAction: "检查恢复后的成果，或提出另一项变更。",
      },
    },
    courseRequestInFlight: {
      message: "Coogo 正在处理你的请求。",
      nextAction: "在结果返回前请保持此任务打开。",
    },
    appliedNextActionCreator: "继续聊天或查看已更改的内容。",
    affectedOutputs: "受影响的成果",
    pendingImpact: (label, count) => `${label}，${count}项变更等待审批`,
    changes: (count) => `${count}项计划变更`,
    reviewAndApprove: "查看影响并审批",
    reviewImpact: "查看影响",
    hideImpact: "收起影响",
    coordinatedApproval: "审批适用于这一整套协同变更计划。",
    preparedImpactSummary: (count) => `已准备${count}项可核对的准确变更。`,
    changeItem: (index) => `变更 ${index}`,
    noImpact: "没有已准备的成果变更。",
    approvalBlockedNoEvidence:
      "提案没有准确变更路径，因此无法批准。请取消并请求新的提案。",
    before: "变更前",
    after: "变更后",
    typeToConfirm: (phrase) => `输入${phrase}以继续`,
    typeToConfirmHint: "要继续，请在下方输入框中原样输入以下短语：",
    approve: "批准计划",
    cancel: "取消",
    applying: "正在应用…",
    cancelling: "正在取消…",
    approvalFailed: "Studio无法应用已批准的计划。",
    cancellationFailed: "此审批已不可用。",
    approvalApplied: "已应用批准的计划，并开始核对结果。",
    approvalCancelled: "提案已取消，未应用任何变更。",
    confirmationFailure: {
      CONFIRMATION_MISSING_OR_EXPIRED: "审批不存在或已过期。请请求新的提案。",
      CONFIRMATION_SCOPE_MISMATCH: "此审批属于另一个项目或Studio会话。",
      CONFIRMATION_PHRASE_MISMATCH: "所需确认短语不匹配。",
      CONFIRMATION_STALE: "提案与当前状态不一致。请请求新的提案并重新查看。",
      CONFIRMATION_STALE_AT_COMMIT:
        "由于文档在写入前一刻发生了变化，此更改未能应用。未应用任何内容。请请求新的提案并重新查看。",
      CONFIRMATION_DOCUMENT_LOCKED:
        "另一项已批准的更改目前正在应用于此文档。请等待其完成后再重新批准。",
      CONFIRMATION_IN_FLIGHT: "此提案已在应用中。",
      DURABLE_JOURNAL_REQUIRED: "批准此操作前请重新登录。",
    },
  },
  nodeStatus: {
    pending: "未执行",
    running: "进行中",
    completed: "已完成",
    failed: "失败",
  },
  planStatus: {
    executed: "已完成",
    failed: "需要处理",
    undone: "已撤销",
    running: "进行中",
    suspended: "已暂停",
    cancelled: "已取消",
  },
  source: {
    "chat-intent-chunk": "聊天请求",
    "chat-operation": "聊天操作",
    webmcp: "WebMCP",
    "studio-ui": "Studio",
  },
  approval: {
    "not-required": "无需审批",
    "awaiting-user-approval": "等待用户审批",
    "not-approved": "未批准",
    "studio-user-action": "已在Studio批准",
    "webmcp-user-approval": "已通过WebMCP批准",
  },
  rollback: {
    notRequired: { label: "无需回滚", detail: "本次执行无需自动回滚。" },
    completed: { label: "已完成", detail: "Studio已恢复本次执行前的状态。" },
    noRetainedChange: {
      label: "没有保留的变更",
      detail: "失败的执行没有留下需要恢复的变更。",
    },
    failed: "失败",
    unavailable: "不可用",
    failureFallback: "Studio无法自动恢复之前的状态。",
  },
  recovery: {
    failedRestored:
      "之前的状态是安全的。请检查失败节点，修正请求或输入后开始新的执行。",
    failedUndo:
      "自动恢复未完成。重试前请使用下方撤销恢复最后保留的Studio变更。",
    failedUnavailable:
      "自动恢复未完成且本次执行无法恢复。请检查错误后发起修正后的请求。",
    undone: "本次执行已撤销。准备好应用修正变更后，请开始新的请求。",
    running: "已从持久日志恢复本次执行。继续或取消前请检查最后记录的节点。",
    suspended: "本次执行已暂停。明确决定继续前，请检查权限和记录状态。",
    cancelled: "本次执行已取消。如仍需应用变更，请开始新的请求。",
    completed:
      "本次执行已完成。已完成的执行无法暂停或恢复，其他变更请开始新的请求。",
  },
  timeline: {
    latestRecordedExecution: "最近记录的执行",
    plan: "计划",
    steps: "步骤",
    readback: "验证",
    rollback: "回滚",
    captured: (count) => `已保留${count}项`,
    verified: (count) => `已验证${count}项`,
    notRecorded: "未记录",
    stateCaptured: "已记录状态",
    executionSteps: (label) => `${label}的执行步骤`,
    stepFallback: (index) => `步骤${index}`,
    statusAt: (status, occurredAt) =>
      occurredAt ? `${status} · ${occurredAt}` : status,
    readbackCaptured: "已保留验证证据。",
    readbackVerifiedNotRetained:
      "验证成功，但证据未保留在持久记录中。",
    readbackFailed: "验证失败；本步骤没有确认的证据。",
    completedWithoutReadback: "已完成，未发起单独的验证。",
    stepErrorFallback: "该步骤返回错误。",
    noResult: "未记录该步骤的结果。",
    authorityAndApproval: "权限与审批",
    runStopped: "执行在完成前停止",
    nextStep: "下一步",
    undoError: (error) => `撤销未完成：${error}`,
    restoring: "正在恢复之前的状态",
    undoLastChange: "撤销上次变更",
    technicalProvenance: "技术来源信息",
    provenancePlan: "计划",
    provenanceRun: "执行",
    provenanceChanges: "变更",
    exactPathsUnavailable: (tools) => `未记录准确路径 · ${tools}`,
    provenanceProject: "项目",
    noProjectScope: "未记录项目范围",
    provenanceRecorded: "记录时间",
    provenanceStateBefore: "变更前状态",
    provenanceStateAfter: "变更后状态",
  },
  errors: {
    checkpoint_authority_mismatch:
      "Studio无法根据已记录的检查点确认此更改。请请求新的提案。",
    CONFIRMATION_STALE: "提案与当前状态不一致。请请求新的提案并重新查看。",
    durable_orchestration_auth_required: "请重新登录以继续此持久操作。",
    exact_impact_invalid: "Studio收到了无效的变更记录，无法继续。",
    exact_impact_unavailable: "Studio无法读取此步骤的准确变更记录。",
    execution_lease_active: "此Studio会话已持有本次执行的锁定。",
    execution_lease_lost: "Studio失去了对本次执行的执行锁定。请请求新的提案。",
    handler_not_registered: "Studio不支持此操作。",
    invalid_durable_event: "Studio在此次执行历史中发现了无效事件。",
    invalid_durable_plan: "Studio为此次执行发现了无效的已记录计划。",
    invalid_transition: "Studio拒绝了此次执行中的一个无效步骤。",
    journal_checkpoint_inconsistent: "Studio已记录的执行历史不一致。",
    journal_receipt_mismatch: "Studio已记录的确认与此次执行不匹配。",
    journal_replay_receipt_required:
      "Studio无法确认重试的步骤是否被准确记录了一次。",
    journal_run_mismatch: "此记录不属于当前执行。",
    lease_receipt_mismatch: "Studio的执行锁定确认与此次执行不匹配。",
    manual_recovery_required: "Studio无法自动恢复此次执行，需要人工检查。",
    mutation_outcome_evidence_missing:
      "Studio缺少确认此更改结果所需的证据。",
    mutation_outcome_not_unknown: "此次执行的结果已经确定，无法重新分类。",
    resume_authority_invalid: "Studio未能找到恢复此次执行所需的批准。",
    resume_authority_rejected: "Studio拒绝了恢复此次执行的批准。",
    resume_grant_binding_mismatch: "此恢复批准与当前执行不匹配。",
    resume_grant_checkpoint_mismatch:
      "此恢复批准与执行的已记录检查点不匹配。",
    studio_tool_failed: "Studio未能完成此更改。",
    undo_ownership_missing: "Studio未收到撤销此更改所需的权限。",
    unsafe_transition_material: "Studio阻止了可能暴露敏感信息的步骤。",
    unknown: "Studio遇到意外问题。请重试或请求新的提案。",
  },
};

/**
 * Traditional Chinese, authored — NOT `{ ...zh }`.
 *
 * This object used to be the Simplified `zh` object with one field overridden,
 * so ~100 creator-facing strings rendered Simplified Chinese in a Traditional
 * session. That is the `{ ...zh }` CLASS: every key nobody remembers to
 * override leaks, and a key added to `zh` tomorrow leaks with nobody touching
 * this file. Spelling the whole object out is what makes the leak impossible
 * rather than merely absent today; the class guard in
 * `src/__tests__/locale-cluster/` fails the build if a Simplified-only
 * character reappears here.
 *
 * Terminology is Taiwan usage, not transliterated Mainland usage: 專案
 * (project), 建立 (create), 發佈 (release), 工作階段 (session), 還原 (undo),
 * 復原 (restore), 套用 (apply), 元件 (component).
 */
const zhTW: StudioExecutionCopy = {
  taskCenter: {
    restoreFailure: "Studio 無法還原先前的狀態。",
    latestExecution: (count) => `最近執行 · ${count} 個事件`,
    loadingDetails: "正在載入執行詳細資料…",
    attentionTitle: "查看這項變更",
    doneReviewing: "完成查看",
    continueInChat: "在聊天中繼續",
    targetUnavailable: "原始目標已無法使用",
    undoFailureReasons: {
      executionNotFound: "找不到該執行記錄。",
      notLatestUndoable: "只能還原最近一次可還原的執行。",
      undoStackChanged:
        "此次執行後全域還原記錄已變更。請先還原中間的操作。",
    },
    state: {
      working: "處理中",
      changed: "可供查看",
      attention: "需要決定",
      blocked: "已阻擋",
      completed: "已完成",
    },
  },
  commandDeck: {
    goal: "目標",
    goalMissing: "請在聊天中設定專案目標。",
    currentCourse: "目前進度",
    describeNext: "請在聊天中說明期望的結果。",
    phase: {
      understanding: "正在理解",
      "needs-input": "需要輸入",
      ready: "已就緒",
      "awaiting-confirmation": "審核變更",
      running: "進行中",
      applied: "已套用",
      verified: "已驗證",
      blocked: "已阻擋",
      failed: "需要處理",
      undone: "已還原",
    },
    course: {
      understanding: {
        message: "Coogo 已準備好把目標整理為協同執行計畫。",
        nextAction: "請在聊天中說明期望的結果。",
      },
      "needs-input": {
        message: "請選擇 Coogo 需要處理的成果或來源。",
        nextAction: "在 Studio 中選取目標，或在聊天中指明名稱。",
      },
      ready: {
        message: "所要求的 Studio 操作可以產生計畫。",
        nextAction: "說明期望結果並查看提案。",
      },
      "awaiting-confirmation": {
        message: "協同變更計畫已準備好。未經審核不會套用任何變更。",
        nextAction: "查看確切影響後核准或取消。",
      },
      running: {
        message: "Coogo 正在套用已核准的計畫並核對結果。",
        nextAction: "請讓這項工作保持開啟，直到回讀完成。",
      },
      applied: {
        message: "最近的協同計畫已套用。請檢查目前的成果。",
        nextAction: "繼續聊天或查看執行證據。",
      },
      verified: {
        message: "回讀已驗證最近的協同變更。",
        nextAction: "繼續聊天或查看保留的證據。",
      },
      blocked: {
        message: "目前進度需要決策或缺少的輸入。",
        nextAction: "繼續前請查看阻擋原因。",
      },
      failed: {
        message: "繼續前需要處理最近一次執行的結果。",
        nextAction: "查看錯誤並要求修正後的提案。",
      },
      undone: {
        message: "最近套用的變更已還原。",
        nextAction: "檢查還原後的成果，或提出另一項變更。",
      },
    },
    courseRequestInFlight: {
      message: "Coogo 正在處理你的請求。",
      nextAction: "在結果回傳前請保持此工作開啟。",
    },
    appliedNextActionCreator: "繼續聊天或查看已變更的內容。",
    affectedOutputs: "受影響的成果",
    pendingImpact: (label, count) => `${label}，${count} 項變更等待審核`,
    changes: (count) => `${count} 項計畫變更`,
    reviewAndApprove: "查看影響並審核",
    reviewImpact: "查看影響",
    hideImpact: "收合影響",
    coordinatedApproval: "審核適用於這一整套協同變更計畫。",
    preparedImpactSummary: (count) => `已準備 ${count} 項可核對的確切變更。`,
    changeItem: (index) => `變更 ${index}`,
    noImpact: "沒有已準備的成果變更。",
    approvalBlockedNoEvidence:
      "提案沒有確切的變更路徑，因此無法核准。請取消並要求新的提案。",
    before: "變更前",
    after: "變更後",
    typeToConfirm: (phrase) => `輸入${phrase}以繼續`,
    typeToConfirmHint: "要繼續，請在下方輸入框中原樣輸入以下短語：",
    approve: "核准計畫",
    cancel: "取消",
    applying: "正在套用…",
    cancelling: "正在取消…",
    approvalFailed: "Studio 無法套用已核准的計畫。",
    cancellationFailed: "這項審核已無法使用。",
    approvalApplied: "已套用核准的計畫，並開始核對結果。",
    approvalCancelled: "提案已取消，未套用任何變更。",
    confirmationFailure: {
      CONFIRMATION_MISSING_OR_EXPIRED: "審核不存在或已過期。請要求新的提案。",
      CONFIRMATION_SCOPE_MISMATCH: "這項審核屬於另一個專案或 Studio 工作階段。",
      CONFIRMATION_PHRASE_MISMATCH: "所需的確認詞句不相符。",
      CONFIRMATION_STALE: "提案與目前狀態不一致。請要求新的提案並重新查看。",
      CONFIRMATION_STALE_AT_COMMIT:
        "由於文件在寫入前一刻發生變更，這項變更未能套用。未套用任何內容。請要求新的提案並重新查看。",
      CONFIRMATION_DOCUMENT_LOCKED:
        "另一項已核准的變更目前正在套用於這份文件。請等待其完成後再重新核准。",
      CONFIRMATION_IN_FLIGHT: "這項提案已在套用中。",
      DURABLE_JOURNAL_REQUIRED: "核准這項操作前請重新登入。",
    },
  },
  nodeStatus: {
    pending: "未執行",
    running: "進行中",
    completed: "已完成",
    failed: "失敗",
  },
  planStatus: {
    executed: "已完成",
    failed: "需要處理",
    undone: "已還原",
    running: "進行中",
    suspended: "已暫停",
    cancelled: "已取消",
  },
  source: {
    "chat-intent-chunk": "聊天請求",
    "chat-operation": "聊天操作",
    webmcp: "WebMCP",
    "studio-ui": "Studio",
  },
  approval: {
    "not-required": "無需審核",
    "awaiting-user-approval": "等待使用者審核",
    "not-approved": "未核准",
    "studio-user-action": "已在 Studio 核准",
    "webmcp-user-approval": "已透過 WebMCP 核准",
  },
  rollback: {
    notRequired: { label: "無需復原", detail: "這次執行無需自動復原。" },
    completed: { label: "已完成", detail: "Studio 已還原這次執行前的狀態。" },
    noRetainedChange: {
      label: "沒有保留的變更",
      detail: "失敗的執行沒有留下需要還原的變更。",
    },
    failed: "失敗",
    unavailable: "無法使用",
    failureFallback: "Studio 無法自動還原先前的狀態。",
  },
  recovery: {
    failedRestored:
      "先前的狀態是安全的。請檢查失敗的節點，修正要求或輸入後開始新的執行。",
    failedUndo:
      "自動還原未完成。重試前請使用下方的還原，復原最後保留的 Studio 變更。",
    failedUnavailable:
      "自動還原未完成，且這次執行無法復原。請檢查錯誤後提出修正後的要求。",
    undone: "這次執行已還原。準備好套用修正變更後，請開始新的要求。",
    running: "已從持久記錄復原這次執行。繼續或取消前請檢查最後記錄的節點。",
    suspended: "這次執行已暫停。明確決定繼續前，請檢查權限和記錄狀態。",
    cancelled: "這次執行已取消。如仍需套用變更，請開始新的要求。",
    completed:
      "這次執行已完成。已完成的執行無法暫停或復原，其他變更請開始新的要求。",
  },
  timeline: {
    latestRecordedExecution: "最近記錄的執行",
    plan: "計畫",
    steps: "步驟",
    readback: "驗證",
    rollback: "復原",
    captured: (count) => `已保留 ${count} 項`,
    verified: (count) => `已驗證 ${count} 項`,
    notRecorded: "未記錄",
    stateCaptured: "已記錄狀態",
    executionSteps: (label) => `${label}的執行步驟`,
    stepFallback: (index) => `步驟 ${index}`,
    statusAt: (status, occurredAt) =>
      occurredAt ? `${status} · ${occurredAt}` : status,
    readbackCaptured: "已保留驗證證據。",
    readbackVerifiedNotRetained: "驗證成功，但證據未保留在持久記錄中。",
    readbackFailed: "驗證失敗；這個步驟沒有確認過的證據。",
    completedWithoutReadback: "已完成，未另外進行驗證。",
    stepErrorFallback: "這個步驟回傳了錯誤。",
    noResult: "未記錄這個步驟的結果。",
    authorityAndApproval: "權限與審核",
    runStopped: "執行在完成前停止",
    nextStep: "下一步",
    undoError: (error) => `還原未完成：${error}`,
    restoring: "正在還原先前的狀態",
    undoLastChange: "還原上次變更",
    technicalProvenance: "技術來源資訊",
    provenancePlan: "計畫",
    provenanceRun: "執行",
    provenanceChanges: "變更",
    exactPathsUnavailable: (tools) => `未記錄確切路徑 · ${tools}`,
    provenanceProject: "專案",
    noProjectScope: "未記錄專案範圍",
    provenanceRecorded: "記錄時間",
    provenanceStateBefore: "變更前狀態",
    provenanceStateAfter: "變更後狀態",
  },
  errors: {
    checkpoint_authority_mismatch:
      "Studio 無法根據已記錄的檢查點確認這項變更。請要求新的提案。",
    CONFIRMATION_STALE: "提案與目前狀態不一致。請要求新的提案並重新查看。",
    durable_orchestration_auth_required: "請重新登入以繼續這項持久操作。",
    exact_impact_invalid: "Studio 收到無效的變更記錄，無法繼續。",
    exact_impact_unavailable: "Studio 無法讀取這個步驟的確切變更記錄。",
    execution_lease_active: "這個 Studio 工作階段已持有這次執行的鎖定。",
    execution_lease_lost:
      "Studio 失去了這次執行的執行鎖定。請要求新的提案。",
    handler_not_registered: "Studio 不支援這項操作。",
    invalid_durable_event: "Studio 在這次執行歷程中發現無效事件。",
    invalid_durable_plan: "Studio 為這次執行發現了無效的已記錄計畫。",
    invalid_transition: "Studio 拒絕了這次執行中的一個無效步驟。",
    journal_checkpoint_inconsistent: "Studio 已記錄的執行歷程不一致。",
    journal_receipt_mismatch: "Studio 已記錄的確認與這次執行不符。",
    journal_replay_receipt_required:
      "Studio 無法確認重試的步驟是否確實只被記錄一次。",
    journal_run_mismatch: "這項記錄不屬於目前的執行。",
    lease_receipt_mismatch: "Studio 的執行鎖定確認與這次執行不符。",
    manual_recovery_required:
      "Studio 無法自動復原這次執行，需要人工檢查。",
    mutation_outcome_evidence_missing:
      "Studio 缺少確認這項變更結果所需的證據。",
    mutation_outcome_not_unknown: "這次執行的結果已經確定，無法重新分類。",
    resume_authority_invalid: "Studio 找不到繼續這次執行所需的核准。",
    resume_authority_rejected: "Studio 拒絕了繼續這次執行的核准。",
    resume_grant_binding_mismatch: "這項繼續核准與目前的執行不符。",
    resume_grant_checkpoint_mismatch:
      "這項繼續核准與執行的已記錄檢查點不符。",
    studio_tool_failed: "Studio 未能完成這項變更。",
    undo_ownership_missing: "Studio 未收到還原這項變更所需的權限。",
    unsafe_transition_material: "Studio 阻擋了可能外洩敏感資訊的步驟。",
    unknown: "Studio 發生非預期的問題。請重試，或要求新的提案。",
  },
};

const COPY: Record<LocaleCode, StudioExecutionCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getStudioExecutionCopy(
  locale: LocaleCode,
): StudioExecutionCopy {
  return COPY[locale];
}

export interface ResolvedStudioExecutionErrorCopy {
  /** Localized text safe to show as the primary line — never raw English. */
  primary: string;
  /** The error's `code`, when it carried one, for diagnostics/telemetry. */
  code?: string;
  /**
   * The underlying `Error.message` (or string error), kept reachable as
   * secondary diagnostic detail so the raw signal is never lost — it must
   * not be shown as the primary line.
   */
  detail?: string;
}

/**
 * Exported (fix-round-1, I3) so callers that persist or narrow a
 * CoogoExecutionError.code — e.g. studio-capability-gateway.ts's
 * StudioCapabilityExecutionFailureCode boundary — can validate against the
 * same closed set this module resolves copy from, instead of casting an
 * arbitrary string into a closed-claiming type.
 */
export function isKnownStudioExecutionErrorCode(
  code: string,
): code is StudioExecutionErrorCode {
  return (STUDIO_EXECUTION_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * The two closed code sets a FAILED capability confirmation can arrive with,
 * as the chat capability card and the Task Center actually receive them from
 * `studio-capability-gateway.ts`:
 *   - `code`  — a `StudioCapabilityFailureCode` (the confirmation gate's own
 *     verdict: phrase mismatch, scope mismatch, stale, document locked, …),
 *     resolved from `commandDeck.confirmationFailure`.
 *   - `executionFailureCode` — a `StudioCapabilityExecutionFailureCode`
 *     (whatever `CoogoExecutionError.code` the failed durable node carried,
 *     narrowed by `narrowExecutionFailureCode()`), resolved from `errors`.
 *
 * P2 (finding 2) exists because the DURABLE orchestration path populates the
 * SECOND field, and the chat capability card's `confirm()` was showing that
 * failure's raw `Error.message` instead:
 * `fail('The approved exact impact is stale.', 'CONFIRMATION_STALE')`
 * (`features/chat/services/studio-orchestration-node-handlers.ts:431`) landed
 * one untranslated English sentence in an otherwise fully-Korean recovery
 * card — measured live by the 2026-08-18 chat-journey census. The copy for
 * that exact code was already sitting in `errors.CONFIRMATION_STALE`; nothing
 * read it on this path.
 *
 * Returns `null` when NEITHER field carries a resolvable code, so the caller
 * supplies its own generic localized line rather than this module inventing
 * one. `'unknown'` and `'mutation_outcome_unknown'` both resolve to
 * `errors.unknown` — the same contract `resolveStudioExecutionErrorCopy`
 * already documents for an unrecognized code.
 */
export function resolveStudioConfirmationFailureCopy(
  copy: StudioExecutionCopy,
  failure: {
    code?: string | null;
    executionFailureCode?: string | null;
  },
): string | null {
  const gateCode = failure.code;
  if (gateCode && gateCode in copy.commandDeck.confirmationFailure) {
    return copy.commandDeck.confirmationFailure[
      gateCode as keyof StudioExecutionCopy["commandDeck"]["confirmationFailure"]
    ];
  }
  const executionCode = failure.executionFailureCode;
  if (!executionCode) return null;
  return isKnownStudioExecutionErrorCode(executionCode)
    ? copy.errors[executionCode]
    : copy.errors.unknown;
}

/**
 * Resolves an error at the display boundary (Task Center / execution
 * surfaces) to locale-appropriate copy. A `CoogoExecutionError`-shaped value
 * (duck-typed on `.code`) with a code enumerated in
 * `STUDIO_EXECUTION_ERROR_CODES` resolves to that code's localized line. Any
 * other error — an unknown/future code, or no code at all — resolves to the
 * generic localized `errors.unknown` line. The raw code/message is preserved
 * on the return value as secondary diagnostic detail; it must never be used
 * as the primary line shown to the customer.
 */
export function resolveStudioExecutionErrorCopy(
  copy: StudioExecutionCopy,
  error: unknown,
): ResolvedStudioExecutionErrorCopy {
  const code =
    error &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as { code: unknown }).code === "string"
      ? (error as { code: string }).code
      : undefined;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : error &&
            typeof error === "object" &&
            "message" in error &&
            typeof (error as { message: unknown }).message === "string"
          ? (error as { message: string }).message
          : undefined;
  const primary =
    code && isKnownStudioExecutionErrorCode(code)
      ? copy.errors[code]
      : copy.errors.unknown;
  return {
    primary,
    ...(code ? { code } : {}),
    ...(message ? { detail: message } : {}),
  };
}
